import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFilter } from '../contexts/FilterContext';
import type { FilterType } from '../contexts/FilterContext';
import type { ProjectStatus } from '../types/project';
import {
  FiChevronDown as ChevronDown,
  FiFilter as Filter,
  FiFolder as Folder,
  FiLayers as Layers,
  FiCloud as Cloud,
  FiServer as Server,
  FiSearch as Search,
} from 'react-icons/fi';

const statusColors: Record<ProjectStatus, string> = {
  planning: 'bg-blue-400',
  active: 'bg-green-400',
  completed: 'bg-gray-400',
  on_hold: 'bg-yellow-400',
  cancelled: 'bg-red-400',
};

const FILTER_TYPE_LABELS: Record<FilterType, string> = {
  all: 'All',
  workload: 'Workload',
  project: 'Project',
  account: 'Account',
  environment: 'Environment',
};

const GlobalFilterSelector: React.FC = () => {
  const {
    filterType,
    selectedId,
    projects,
    workloads,
    accounts,
    environments,
    isLoading,
    selectFilter,
    hasProjectAccess,
  } = useFilter();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  if (!hasProjectAccess) return null;
  if (isLoading) {
    return (
      <div className="relative mr-4">
        <div className="flex items-center px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Build display label: "Type: Name" or "All"
  let displayLabel = 'All';
  if (filterType !== 'all' && selectedId) {
    const typePrefix = FILTER_TYPE_LABELS[filterType];
    let itemName = '';
    if (filterType === 'project') {
      itemName = projects.find(p => p.id === selectedId)?.name || '';
    } else if (filterType === 'workload') {
      itemName = workloads.find(w => w.id === selectedId)?.name || '';
    } else if (filterType === 'account') {
      itemName = accounts.find(a => a.id === selectedId)?.account_id || '';
    } else if (filterType === 'environment') {
      itemName = environments.find(e => e.id === selectedId)?.name || '';
    }
    displayLabel = itemName ? `${typePrefix}: ${itemName}` : 'All';
  }

  const handleSelect = (type: FilterType, id: string | null) => {
    selectFilter(type, id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const query = searchQuery.toLowerCase();
  const filteredWorkloads = query ? workloads.filter(w => w.name.toLowerCase().includes(query)) : workloads;
  const filteredProjects = query ? projects.filter(p => p.name.toLowerCase().includes(query)) : projects;
  const filteredAccounts = query ? accounts.filter(a => a.account_id.includes(query)) : accounts;
  const filteredEnvironments = query ? environments.filter(e => e.name.toLowerCase().includes(query)) : environments;

  const isSelected = (type: FilterType, id: string) => filterType === type && selectedId === id;

  return (
    <div className="relative mr-4" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
      >
        <Filter className="w-4 h-4 mr-2" />
        <span className="max-w-[200px] truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-50 max-h-[70vh] flex flex-col">
          {/* Search */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="overflow-y-auto py-1">
            {/* All */}
            {!searchQuery && (
              <button
                onClick={() => handleSelect('all', null)}
                className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${
                  filterType === 'all'
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
            )}

            {/* 1. Workloads */}
            {(filteredWorkloads.length > 0 || !searchQuery) && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-2">
                  <Layers className="w-3 h-3" />
                  Workloads
                </div>
                {filteredWorkloads.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                    No workloads defined{' '}
                    <Link to="/hub/workloads" className="text-blue-500 hover:underline" onClick={() => setIsOpen(false)}>
                      Create one
                    </Link>
                  </div>
                ) : (
                  filteredWorkloads.map((workload) => (
                    <button
                      key={workload.id}
                      onClick={() => handleSelect('workload', workload.id)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        isSelected('workload', workload.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{workload.name}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-2 shrink-0">
                          {workload.tag_pairs.length} tag{workload.tag_pairs.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </>
            )}

            {/* 2. Projects */}
            {(filteredProjects.length > 0 || !searchQuery) && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-2">
                  <Folder className="w-3 h-3" />
                  Projects
                </div>
                {filteredProjects.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                    No projects yet{' '}
                    <Link to="/hub/projects" className="text-blue-500 hover:underline" onClick={() => setIsOpen(false)}>
                      Create one
                    </Link>
                  </div>
                ) : (
                  filteredProjects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleSelect('project', project.id)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        isSelected('project', project.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 shrink-0 ${statusColors[project.status] || 'bg-gray-400'}`} />
                        <span className="truncate">{project.name}</span>
                      </div>
                    </button>
                  ))
                )}
              </>
            )}

            {/* 3. Accounts */}
            {(filteredAccounts.length > 0 || !searchQuery) && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-2">
                  <Cloud className="w-3 h-3" />
                  Accounts
                </div>
                {filteredAccounts.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                    No AWS accounts linked{' '}
                    <Link to="/hub/aws-environments" className="text-blue-500 hover:underline" onClick={() => setIsOpen(false)}>
                      Create one
                    </Link>
                  </div>
                ) : (
                  filteredAccounts.map((account) => (
                    <button
                      key={account.id}
                      onClick={() => handleSelect('account', account.id)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        isSelected('account', account.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <span className="font-mono">{account.account_id}</span>
                      <span className="ml-2 text-xs text-gray-400">({account.account_type})</span>
                    </button>
                  ))
                )}
              </>
            )}

            {/* 4. Environments */}
            {(filteredEnvironments.length > 0 || !searchQuery) && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-200 dark:border-gray-600 mt-1 pt-2">
                  <Server className="w-3 h-3" />
                  Environments
                </div>
                {filteredEnvironments.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                    No environments configured{' '}
                    <Link to="/hub/aws-environments" className="text-blue-500 hover:underline" onClick={() => setIsOpen(false)}>
                      Create one
                    </Link>
                  </div>
                ) : (
                  filteredEnvironments.map((env) => (
                    <button
                      key={env.id}
                      onClick={() => handleSelect('environment', env.id)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        isSelected('environment', env.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      <span>{env.name}</span>
                      {env.environment_type && (
                        <span className="ml-2 text-xs text-gray-400">({env.environment_type})</span>
                      )}
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalFilterSelector;
